import type { FC } from 'react';
import { useCallback, useDeferredValue, useEffect, useState } from 'react';

import { Button, Col, Row, Select, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconPlus from '@/assets/icons/icon-plus.svg?react';
import Card from '@/components/RCard';
import PageHeader from '@/components/RPageHeader';
import SearchInput from '@/components/RSearchInput';
import UpdateTime from '@/components/RUpdateTime';
import { RUsageLimitIndicator } from '@/components/RUsageLimitIndicator';
import useAuth from '@/hooks/useAuth';
import useProductTour from '@/hooks/useProductTour';
import useScenariosTableColumns from '@/hooks/useScenariosTableColumns';
import { DrawerType } from '@/types/global';
import type {
  QueryParamsFilterProps,
  Scenario,
  ScenariosResponse,
} from '@/types/scenario';
import { createQueryString, hasNonEmptyValues } from '@/utils';

import ScenarioCreateDrawer from './Drawer/ScenarioCreateDrawer';
import ScenarioGenericDrawer from './Drawer/ScenarioGenericDrawer';
import StyledScenarios from './styles';

const Scenarios: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const [nameSearchValue, setNameSearchValue] = useState<string>();
  const [selectedActiveStatus, setSelectedActiveStatus] = useState<
    'all' | 'active' | 'inactive'
  >('active');
  const [queryParams, setQueryParams] = useState<QueryParamsFilterProps>({
    name: '',
    isActive: undefined,
  });
  const queryString = createQueryString(queryParams);

  const scenariosCreateRef = useProductTour('createScenario');

  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState<boolean>(false);

  const deferredNameSearch = useDeferredValue(nameSearchValue);
  const [pageSize, setPageSize] = useState(7);
  const [offset, setOffset] = useState(1);

  useEffect(() => {
    setQueryParams((prev) => ({
      ...prev,
      name: deferredNameSearch || '',
      isActive: selectedActiveStatus,
    }));
  }, [deferredNameSearch, selectedActiveStatus]);

  const {
    data: scenarioData,
    isLoading,
    mutate,
  } = useSWR<ScenariosResponse>(
    hasNonEmptyValues(queryParams)
      ? `/scenario/?limit=${pageSize}&offset=${
          (offset - 1) * pageSize
        }&${queryString}`
      : null
  );

  const [drawer, setDrawer] = useState<DrawerType<Scenario>>({
    type: null,
    data: null,
  });
  const updateDrawer = useCallback(
    (
      type: DrawerType<Scenario>['type'],
      data: DrawerType<Scenario>['data']
    ) => {
      setDrawer({ type, data });
    },
    []
  );

  const scenariosTableColumns = useScenariosTableColumns(mutate, updateDrawer);
  const assetLimit = user?.organization.subscription.plan.simAssetLimit;

  const handlePaginationChange = (page: number) => {
    setOffset(page);
  };

  const handleCreateDrawerOpen = () => {
    setIsCreateDrawerOpen(true);
  };

  return (
    <StyledScenarios>
      <PageHeader
        title={t('scenario.pageTitle')}
        description={t('scenario.pageDescription')}
        rightSideSection={<RUsageLimitIndicator.Scenarios />}
      />
      <Card title={t('scenario.allScenarios.title')}>
        <Row
          justify="space-between"
          className="filter-header"
          style={{ padding: '22px' }}
        >
          <Col className="left-side">
            <Row>
              <Col>
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <SearchInput
                    placeholder={t('scenario.allScenarios.searchPlaceholder')}
                    onChange={(e) => setNameSearchValue(e.target.value)}
                  />
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <Select
                    defaultValue={selectedActiveStatus}
                    placeholder={t('scenario.allScenarios.statusPlaceholder')}
                    style={{ width: 180 }}
                    onChange={(val) =>
                      setSelectedActiveStatus(
                        val as 'all' | 'active' | 'inactive'
                      )
                    }
                    options={[
                      {
                        value: 'all',
                        label: t('scenario.allScenarios.statusOptions.all'),
                      },
                      {
                        value: 'active',
                        label: t('scenario.allScenarios.statusOptions.active'),
                      },
                      {
                        value: 'inactive',
                        label: t(
                          'scenario.allScenarios.statusOptions.inactive'
                        ),
                      },
                    ]}
                  />
                </Row>
              </Col>
              <Col className="pl-16">
                <Row style={{ height: '100%' }} align="middle" justify="center">
                  <UpdateTime fetchFunction={mutate} />
                </Row>
              </Col>
            </Row>
          </Col>
          <Col ref={scenariosCreateRef}>
            <Button
              type="primary"
              icon={<IconPlus fill={theme.colors.light} />}
              onClick={() => handleCreateDrawerOpen()}
              data-testid="create-scenario-btn"
            >
              {t('scenario.allScenarios.create')}
            </Button>
          </Col>
        </Row>
        <Table
          rowKey="id"
          columns={scenariosTableColumns}
          dataSource={scenarioData?.results}
          loading={isLoading}
          size="small"
          pagination={{
            total: scenarioData?.count,
            pageSizeOptions: ['7', '10', '20', '50', '100'],
            pageSize: pageSize,
            hideOnSinglePage: true,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            onShowSizeChange: (current, size) => {
              setOffset(current);
              setPageSize(size);
            },
            showSizeChanger: true,
          }}
          scroll={{ x: 900 }}
        />
      </Card>
      {assetLimit && scenarioData && isCreateDrawerOpen && (
        <ScenarioCreateDrawer
          mutate={mutate}
          open={isCreateDrawerOpen}
          setIsOpen={setIsCreateDrawerOpen}
          maxDataCount={assetLimit}
          currentDataCount={scenarioData?.count}
        />
      )}
      {assetLimit && scenarioData && drawer.type && (
        <ScenarioGenericDrawer
          mutate={mutate}
          drawerOptions={drawer}
          setDrawerOptions={setDrawer}
          maxDataCount={assetLimit}
          currentDataCount={scenarioData?.count}
        />
      )}
    </StyledScenarios>
  );
};

export default Scenarios;
